import PropTypes from 'prop-types';
import { useState, memo } from 'react';
// -*- mode: jsx;-*-
import { Svg } from './Svg.js';
import { InstagramSharetool } from './instagram-share/InstagramSharetool.js';
import { PortalWrapper } from './PortalWrapper.js';


/**
 * Given the rendered instagram-sharetool-lightbox, returns what we actually want to embed in this component - nothing
 * (if closed), a portal (if we have a portal-target), or just the lightbox itself.
 */
const renderLightbox = (open, portalTarget, lightbox) => {
    if (!open) {
        return null;
    }

    if (portalTarget) {
        return <PortalWrapper portalTarget={portalTarget}>{lightbox}</PortalWrapper>;
    }

    return lightbox;
}

/**
 * This is overkill *now*, but previous versions of this tool relied on `navigator.share`. And if Instagram un-breaks
 * `navigator.share`, future versions might rely on it as well.
 */
const shouldShow = (forceShow) => {
    // Bail if we're on the server.
    if (typeof navigator === 'undefined') {
        return false;
    }

    // Implicit dependency on mobileOrFullSite.js.
    // Check for `isMobileView`'s existence before calling it (in case we ever call this serverside).
    return forceShow || (window.isMobileView && window.isMobileView());
}

export let InstagramLink = ({forceShow, urlToShare, getTrackingLink, classroomPhotoUrl, profilePhotoUrl, teacherName, projectId, costToComplete, totalPrice, fulfillmentTrailer, className, portalTarget, children}) => {
    const [open, setOpen] = useState(false);

    if (!shouldShow(forceShow)) {
        return null;
    }

    const onClick = (e) => {
        // Don't let the underlying project card onClick happen.
        e.preventDefault();

        setOpen(!open);
    }

    const outerOnClick = (e) => {
        // Just keep the underlying <a> from getting clicked.
        e.preventDefault();
    }

    const buttonContent = children || <Svg icon="instagram" iconFamily="social"/>;

    const projectData = { projectId, classroomPhotoUrl, profilePhotoUrl, teacherName, costToComplete, totalPrice, fulfillmentTrailer };

    // 'ig' instead of 'instagram' because Instagram restricts URL-length in stickers/posts.
    //
    // getTrackingLink assumes its output will be embedded into a query-string parameter, so it does an extra URL-escape.
    // Undo that here.
    const trackingLinkUrl = decodeURIComponent(getTrackingLink(urlToShare, 'ig'));
    const lightbox = <InstagramSharetool urlToShare={trackingLinkUrl} isOpen={open} setIsOpen={setOpen} projectData={projectData} />

    return (
        <span onClick={outerOnClick}>
          <a className={className || 'link-button icon icon-ss-instagram heap-ig-share'} onClick={onClick} aria-label="Share on Instagram">
              {buttonContent}
          </a>
          {renderLightbox(open, portalTarget, lightbox)}
         </span>
    );
}
InstagramLink.propTypes = {
    urlToShare: PropTypes.string.isRequired,
    getTrackingLink: PropTypes.func.isRequired,

    // Teacher stuff!
    classroomPhotoUrl: PropTypes.string.isRequired,
    profilePhotoUrl: PropTypes.string.isRequired,
    teacherName: PropTypes.string.isRequired,

    // Project stuff!
    projectId: PropTypes.number,
    costToComplete: PropTypes.number.isRequired,
    totalPrice: PropTypes.number.isRequired,
    fulfillmentTrailer: PropTypes.string.isRequired,

    className: PropTypes.string,
    // See UiProperties.java
    forceShow: PropTypes.bool,
};
InstagramLink = memo(InstagramLink);
